import { useCallback } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useOnConfirm } from '../../../../contexts/DialogContext';
import { selectOptionFactory } from '../../../../utils';
import Button from '../../../generic/Button';
import { FormLabel, FormLabelVariant } from '../../../generic/Forms';
import { Select } from '../../../generic/Select';
import Subheading from '../../../shared/settings/SettingsPageSubheading';
import { useCanUserDeleteProject, useCanUserMakeProjectPublic } from '../permissions';
import type { SelectOption } from '../../../../types';

import styles from './GeneralSettings.module.less';

const privacyOptions = [
  selectOptionFactory(true, 'Public'),
  selectOptionFactory(false, 'Private'),
];

type Props = {
  locator: string;
  isPublic: boolean;
  isSaving: boolean;
  onUpdatePrivacy: (isPublic: boolean) => void;
  onDeleteProject: () => Promise<void>;
};

/**
 * ProjectGeneralSettingsDangerZone is the section of the general settings page
 * which contains the dangerous settings which can cause data loss/project deletion.
 */
const ProjectGeneralSettingsDangerZone: VoidFunctionComponent<Props> = ({
  locator,
  isPublic,
  isSaving,
  onUpdatePrivacy,
  onDeleteProject,
}) => {
  const onConfirm = useOnConfirm();
  const canDelete = useCanUserDeleteProject(locator);
  const canMakeProjectPublic = useCanUserMakeProjectPublic(locator);
  //  Show a confirmation dialog before changing the project visibility.
  const handleChangePrivacy = useCallback((selectedOption: SelectOption<boolean> | null) => onConfirm({
    title: 'Confirm changing project visibility',
    body: 'Are you sure you want to change how people can access this project? Note that you will still need to save the changes after confirming.',
    onConfirm: () => onUpdatePrivacy(selectedOption?.value === true),
  }), [onConfirm, onUpdatePrivacy]);

  //  Show a confirmation dialog before deleting the project
  const handleDeleteProject = useCallback(() => onConfirm({
    title: 'Delete project forever?',
    body: 'Are you sure you want to permanently delete this project? This cannot be undone.',
    onConfirm: onDeleteProject,
  }), [onConfirm, onDeleteProject]);

  return (
    <div className={styles.dangerZone}>
      <Subheading>
        Danger Zone
      </Subheading>
      <FormLabel label="Project Visibility" variant={FormLabelVariant.Filter}>
        <Select
          id="project-settings-project-visibility"
          onChange={handleChangePrivacy}
          options={privacyOptions}
          value={privacyOptions.find(option => option.value === isPublic)}
          isDisabled={!canMakeProjectPublic || isSaving}
        />
      </FormLabel>
      <div className={styles.dangerZoneDelete}>
        <h5>Delete this project</h5>
        <p className={`${styles.smallText} ${styles.smallTextParagraph}`}>
          Once you delete a project it cannot be recovered.
        </p>
        <Button
          type="error"
          size="large"
          onClick={handleDeleteProject}
          isDisabled={!canDelete || isSaving}
        >
          Delete project
        </Button>
      </div>
    </div>
  );
};

export default ProjectGeneralSettingsDangerZone;
